import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, BookOpen, CalendarHeart, Clock, Package } from "lucide-react";
import { fetchOrder } from "../lib/api";
import { EbookReader, downloadGuide } from "../components/EbookReader";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useConsultEnabled } from "../lib/useConsultEnabled";
import { useFreeConsult } from "../lib/useFreeConsult";
import { startCashfreePayment } from "../lib/cashfree";
import { shopError } from "../lib/shop";
import { formatINR } from "../lib/constants";

export default function OrderConfirmation() {
    const { orderNumber } = useParams();
    const [paying, setPaying] = React.useState(false);
    const { data: order, isLoading } = useQuery({
        queryKey: ["order", orderNumber],
        queryFn: () => fetchOrder(orderNumber),
    });

    if (isLoading) return <div className="pt-32 text-center text-zinc-500">Loading…</div>;
    if (!order) return <div className="pt-32 text-center text-zinc-500">Order not found.</div>;

    const unpaid = order.payment_status !== "paid";
    const payNow = async () => {
        setPaying(true);
        try {
            await startCashfreePayment(order.order_number);
        } catch (e) { toast.error(shopError(e)); setPaying(false); }
    };

    return (
        <div className="pt-28 md:pt-32 pb-20" data-testid="order-confirmation-page">
            <div className="mx-auto max-w-3xl px-6 md:px-12">
                <div className="text-center mb-10">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 ${
                        unpaid ? "bg-gold/15 text-gold" : "bg-lime/15 text-lime glow-lime"}`}>
                        {unpaid ? <Clock className="w-8 h-8" /> : <Check className="w-8 h-8" />}
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">
                        {unpaid ? "Payment Pending" : "Order Confirmed!"}
                    </h1>
                    <p className="mt-3 text-zinc-400">
                        {unpaid
                            ? "Your order is saved and waiting for payment. Nothing has been charged yet."
                            : `Thanks for your order. We'll send updates to ${order.address.email}.`}
                    </p>
                    {unpaid && (
                        <button onClick={payNow} disabled={paying} data-testid="order-pay-now"
                            className="btn-pill btn-primary mt-6 disabled:opacity-50">
                            {paying ? "OPENING SECURE PAYMENT…" : "COMPLETE PAYMENT"}
                        </button>
                    )}
                </div>

                <div className="p-6 md:p-8 rounded-sm bg-bg1 border border-white/10">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Order Number</div>
                            <div className="font-display text-2xl text-lime mt-1" data-testid="order-number">{order.order_number}</div>
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 text-gold text-xs uppercase tracking-widest font-bold">
                            <Package className="w-3.5 h-3.5" /> {order.status}
                        </div>
                    </div>

                    <div className="space-y-3">
                        {order.items.map((it) => (
                            <div key={it.product_id} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                                <div>
                                    <div className="font-semibold">{it.product_name}</div>
                                    <div className="text-xs text-zinc-500">x{it.quantity} · {formatINR(it.unit_price)} each</div>
                                </div>
                                <div className="font-bold">{formatINR(it.subtotal)}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 space-y-2 text-sm">
                        <Row label="Subtotal" value={formatINR(order.subtotal)} />
                        {order.discount > 0 && (
                            <div className="flex justify-between text-zinc-400">
                                <span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span>
                                <span className="text-lime" data-testid="order-discount">- {formatINR(order.discount)}</span>
                            </div>
                        )}
                        <Row label="Shipping" value={order.shipping === 0 ? "Free" : formatINR(order.shipping)} />
                        <div className="border-t border-white/10 pt-3 mt-3 flex justify-between text-lg font-bold">
                            <span>Total</span><span className="text-lime" data-testid="order-total">{formatINR(order.total)}</span>
                        </div>
                        {order.savings > 0 && (
                            <div className="mono text-[10px] tracking-[0.14em] text-lime pt-1" data-testid="order-savings">
                                YOU SAVED {formatINR(order.savings)}
                            </div>
                        )}
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/10">
                        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">Delivering To</div>
                        <div className="text-sm text-zinc-300">
                            {order.address.full_name}<br/>
                            {order.address.address_line1}{order.address.address_line2 ? `, ${order.address.address_line2}` : ""}<br/>
                            {order.address.city}, {order.address.state} {order.address.pincode}<br/>
                            {order.address.country}<br/>
                            <span className="text-zinc-500">{order.address.phone}</span>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Link to="/shop" className="btn-pill btn-secondary flex-1 text-center">Continue Shopping</Link>
                        <Link to="/" className="btn-pill btn-primary flex-1 text-center" data-testid="back-home-button">Back to Home</Link>
                    </div>
                </div>

                <AccountSnapshot />
                <GuidesBlock order={order} />
                <FreeConsultBanner />
                <Suggestions bought={(order.items || []).flatMap((i) => [i.slug, i.product_slug, i.product_id])} />
            </div>
        </div>
    );
}


// The lifetime numbers live here now, not on the account landing page.
// Guides bought in this order — read in the browser or keep a watermarked copy.
const GuidesBlock = ({ order }) => {
    const [reading, setReading] = React.useState(null);
    const { data: products = [] } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)).json(),
        staleTime: 120000,
    });
    const paid = order.payment_status === "paid";
    const guides = (order.items || [])
        .map((i) => products.find((p) => p.shop_type === "ebook"
            && (p.id === i.product_id || p.slug === (i.slug || i.product_slug))))
        .filter(Boolean);
    if (!guides.length) return null;

    return (
        <div className="mt-6 bg-bg1 border border-white/10 p-6" data-testid="order-guides">
            <h2 className="font-display text-xl tracking-tight flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gold" /> Your guides
            </h2>
            <div className="mt-4 space-y-3">
                {guides.map((g) => (
                    <div key={g.slug} className="border border-white/8 p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm font-semibold">{g.name}</div>
                        {!paid ? (
                            <span className="mono text-[10px] tracking-[0.14em] text-zinc-500">UNLOCKS ONCE PAYMENT IS CONFIRMED</span>
                        ) : !g.has_digital_file ? (
                            <span className="mono text-[10px] tracking-[0.14em] text-zinc-500">FILE IS BEING ADDED — WE&apos;LL EMAIL YOU</span>
                        ) : (
                            <div className="flex items-center gap-4">
                                <button onClick={() => setReading({ slug: g.slug, name: g.name, fileName: g.digital_file_name })}
                                    data-testid={`order-read-${g.slug}`} className="btn-pill btn-primary">READ NOW</button>
                                <button onClick={() => downloadGuide(g.slug, g.digital_file_name)}
                                    data-testid={`order-download-${g.slug}`}
                                    className="mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-white">DOWNLOAD</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {reading && <EbookReader {...reading} onClose={() => setReading(null)} />}
        </div>
    );
};


const AccountSnapshot = () => {
    const { customer } = useCustomerAuth();
    if (!customer) return null;
    return (
        <div className="grid grid-cols-2 gap-4 mt-6" data-testid="order-account-snapshot">
            <div className="bg-bg1 border border-white/10 p-5">
                <div className="mono text-[10px] tracking-[0.18em] text-zinc-500">ORDERS PLACED</div>
                <div className="font-display text-3xl mt-2">{customer.total_orders || 0}</div>
            </div>
            <div className="bg-bg1 border border-white/10 p-5">
                <div className="mono text-[10px] tracking-[0.18em] text-zinc-500">TOTAL SPENT</div>
                <div className="font-display text-3xl mt-2 text-gold">{formatINR(customer.total_spent || 0)}</div>
            </div>
        </div>
    );
};


const FreeConsultBanner = () => {
    const { enabled } = useConsultEnabled();
    const { eligible } = useFreeConsult();
    if (!enabled) return null;
    return (
        <div className="mt-6 bg-bg1 border border-gold/30 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="order-free-consult">
            <div className="flex items-start gap-3">
                <CalendarHeart className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                    <div className="font-display text-xl tracking-tight">
                        {eligible ? "Your free consultation" : "Book a consultation"}
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                        {eligible
                            ? "One free session with our practitioner comes with your order. Pick a slot now."
                            : "Need to talk it through again? Book your next session with our practitioner."}
                    </p>
                </div>
            </div>
            <Link to="/consultation/book" data-testid="order-book-consult" className="btn-pill btn-primary shrink-0">
                {eligible ? "BOOK FREE CONSULTATION" : "BOOK A CONSULTATION"}
            </Link>
        </div>
    );
};


const Suggestions = ({ bought = [] }) => {
    const { data: products = [] } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)).json(),
        staleTime: 120000,
    });
    const picks = products.filter((p) => !bought.includes(p.slug) && !bought.includes(p.id)
        && p.purchasable !== false).slice(0, 3);
    if (!picks.length) return null;

    return (
        <div className="mt-6 bg-bg1 border border-white/10 p-6" data-testid="order-suggestions">
            <h2 className="font-display text-xl tracking-tight">You may also like</h2>
            <div className="grid sm:grid-cols-3 gap-4 mt-5">
                {picks.map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} data-testid={`suggestion-${p.slug}`}
                        className="border border-white/8 p-4 hover:border-gold/40 transition-colors">
                        {p.images?.[0] && (
                            <img src={p.images[0]} alt={p.name} className="w-full h-28 object-cover mb-3" />
                        )}
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="mono text-[10px] tracking-[0.14em] text-gold mt-1">
                            {p.shop_type === "ebook" ? "GUIDE · " : ""}{formatINR(p.price)}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

function Row({ label, value }) {
    return (
        <div className="flex justify-between text-zinc-400">
            <span>{label}</span>
            <span className="text-white">{value}</span>
        </div>
    );
}
